import { useEffect, useEffectEvent } from 'react';
import {
  useQuery as _useQuery,
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
  QueryFunction,
  DefaultError,
  isCancelledError,
  CancelledError,
} from '@tanstack/react-query';

type LegacyCallbacks<TData = unknown, TError = unknown> = {
  onSuccess?: (data: TData) => void;
  onError?: (err: TError) => void;
  onSettled?: (data: TData | undefined, err: TError | null) => void;
};

export type UseQueryOptionsWithCallbacks<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> &
  LegacyCallbacks<TData, TError>;

function normalizeQueryKey(key: any): QueryKey {
  return Array.isArray(key) ? key : [key];
}

const _useQueryImpl = <TQueryFnData, TError, TData, TQueryKey extends QueryKey>(
  options: UseQueryOptionsWithCallbacks<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> => {
  const { onSuccess, onError, onSettled, ..._options } = options;

  const queryResult = _useQuery(
    _options as UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  );

  const data = queryResult.data;
  const error = queryResult.error;
  const isSuccess = queryResult.isSuccess;
  const isError = queryResult.isError;

  const onSuccessEvent = useEffectEvent((data: TData) => {
    onSuccess?.(data);
  });

  const onErrorEvent = useEffectEvent((error: TError) => {
    if (error instanceof CancelledError) return;
    onError?.(error);
  });

  const onSettledEvent = useEffectEvent(
    (data: TData | undefined, error: TError | null) => {
      onSettled?.(data, error);
    },
  );

  useEffect(() => {
    if (!isSuccess) return;
    onSuccessEvent(data!);
  }, [data, isSuccess]);

  useEffect(() => {
    if (!isError) return;
    onErrorEvent(error!);
  }, [error, isError]);

  useEffect(() => {
    if (!isSuccess && !isError) return;
    onSettledEvent(data, error);
  }, [data, error, isSuccess, isError]);

  return queryResult;
};

const isObject = (value: any) => {
  return Object.prototype.toString.apply(value) === '[object Object]';
};

type LegacyQueryKey = QueryKey | string | number;

function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends LegacyQueryKey = LegacyQueryKey,
>(
  options: UseQueryOptionsWithCallbacks<TQueryFnData, TError, TData, any>,
): UseQueryResult<TData, TError>;

function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends LegacyQueryKey = LegacyQueryKey,
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, any>,
  options?: Omit<
    UseQueryOptionsWithCallbacks<TQueryFnData, TError, TData, any>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<TData, TError>;

function useQuery<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends LegacyQueryKey,
>(arg1: any, arg2?: any, arg3?: any) {
  const options = isObject(arg1)
    ? { ...arg1, queryKey: normalizeQueryKey(arg1.queryKey) }
    : { ...arg3, queryKey: normalizeQueryKey(arg1), queryFn: arg2 };

  return _useQueryImpl(options);
}

export default useQuery;
